import React, {useCallback, useState} from 'react';
import classes from './FormLogin.module.scss'
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {
  Box, Divider, FormHelperText,
  IconButton,
  InputAdornment,
  TextField, Typography,
} from "@mui/material";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import DefaultButton from '../../ui/default-button/DefaultButton';
import useAuth from '../../../../hooks/auth/useAuth';
import {useTranslation} from "../../../../hooks/translations/useTranslation";
import {toast} from "react-hot-toast";
import {OauthButtons} from "../../__molecules/OauthButtons/OauthButtons";
import {authInputSx} from "../authFieldStyles";


interface IFormInputs {
  username: string;
  password: string;
  submit: string;
}


const FormLogin = () => {

    const {login} = useAuth();

    const t = useTranslation();

    const schema = yup.object({
      username: yup.string()
        .required((t('login.required-name'))),
      password: yup.string()
        .required(t(('login.required-password'))),
    }).required();

    const {
      handleSubmit,
      control,
      setError,
      formState: {errors},
    } = useForm<IFormInputs>({resolver: yupResolver(schema)});

    const submit: SubmitHandler<IFormInputs> = useCallback(
      async (data: any) => {
        await login(data.username, data.password)
          .catch((error) => {
            toast.error(error?.message);
            setError('username', error?.message);
            setError('password', error?.message);
            setError('submit', {
              type: 'server',
              message: error?.message,
            });
          });
      },
      [login, setError]
    );

    const [showPassword, setShowPassword] = useState(false);

    const onToggleShowPassword = useCallback(() => {
      showPassword ? setShowPassword(false) : setShowPassword(true);
    }, [showPassword]);

    return (
      <div className={classes['form-login']}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
          <OauthButtons/>
        </Box>
        <Divider sx={{my: 2}}>
          <Typography variant="caption" color="text.secondary">or sign in with email</Typography>
        </Divider>
        <form noValidate onSubmit={handleSubmit(submit)}>
          <div className={classes['form-login__field']}>
            <label htmlFor="login-username" className={classes['form-login__label']}>
              {t('login.username-label')}
            </label>
            <Controller
              name="username"
              control={control}
              defaultValue=""
              render={({field, fieldState: {isTouched, error}}) => (
                <TextField
                  {...field}
                  id="login-username"
                  fullWidth
                  error={isTouched && !!error}
                  helperText={error?.message}
                  placeholder="you@example.com"
                  variant="outlined"
                  type="text"
                  autoComplete="username"
                  sx={authInputSx}
                />
              )}
            />
          </div>
          <div className={classes['form-login__field']}>
            <label htmlFor="login-password" className={classes['form-login__label']}>
              {t('login.password-label')}
            </label>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({field, fieldState: {isTouched, error}}) => (
                <TextField
                  {...field}
                  id="login-password"
                  fullWidth
                  error={isTouched && !!error}
                  helperText={error?.message}
                  placeholder="••••••••"
                  variant="outlined"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  sx={authInputSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={onToggleShowPassword}
                          edge="end"
                        >
                          {showPassword ? <Visibility/> : <VisibilityOff/>}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </div>
          <Box>
            {errors.submit && (
              <FormHelperText error>{errors.submit.message}</FormHelperText>
            )}
          </Box>

          <Box sx={{mt: 2, gap: 2, display: 'flex', flexDirection: 'column'}}>
            <DefaultButton type={"submit"} className={classes['form-login__button']}>
              {t('login.button-login-label')}
            </DefaultButton>
          </Box>

        </form>
      </div>
    );
  }
;

export default FormLogin;
